import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { site, nav, apartments, ui } from "@/data/site";
import { otherLangHref, LANG } from "@/lib/lang";
import BookButton from "@/components/BookButton";

// Three-line burger that morphs into an X when open. 32px wide, thin modern lines.
function Burger({ open }) {
  const line = "block h-[1.5px] w-[32px] rounded-full bg-current origin-center";
  return (
    <span className="relative flex h-[14px] w-[32px] flex-col items-center justify-between">
      <motion.span
        className={`${line} absolute top-0`}
        animate={open ? { rotate: 45, top: "50%", y: "-50%" } : { rotate: 0, top: 0, y: 0 }}
        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      />
      <motion.span
        className={`${line} absolute top-1/2 -translate-y-1/2`}
        animate={open ? { opacity: 0, scaleX: 0.4 } : { opacity: 1, scaleX: 1 }}
        transition={{ duration: 0.2 }}
      />
      <motion.span
        className={`${line} absolute bottom-0`}
        animate={open ? { rotate: -45, bottom: "50%", y: "50%" } : { rotate: 0, bottom: 0, y: 0 }}
        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      />
    </span>
  );
}

// Routes that open over a full-bleed dark image hero (white burger/logo at top).
const isImageHero = (path) =>
  path === "/" ||
  path === "/about" ||
  path === "/contact" ||
  path === "/discover-milos" ||
  path === "/guides" ||
  path.startsWith("/apartments/") ||
  path.startsWith("/guides/");

export default function Navbar() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const overHero = isImageHero(pathname) && !scrolled;
  const logoSrc = overHero ? site.logoWhite : site.logoColor;
  // Burger: white only over a dark hero with the menu closed; dark otherwise.
  const burgerColor = overHero && !open ? "text-white" : "text-ink";

  // GR / Book pills: frosted glass so they read over hero or the white bar.
  const cardCls = "bg-white/70 text-ink ring-1 ring-ink/10 backdrop-blur";

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-[70] px-[10px] pt-[10px]" data-testid="site-navbar">
        <motion.div
          initial={false}
          animate={{
            backgroundColor: scrolled ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0)",
            boxShadow: scrolled
              ? "0 14px 40px rgba(78,78,78,0.16)"
              : "0 0 0 rgba(0,0,0,0)",
          }}
          transition={{ duration: 0.35 }}
          className={`rounded-[16px] transition-all duration-300 ${
            scrolled ? "ring-1 ring-ink/[0.06] backdrop-blur-xl" : "ring-0"
          }`}
        >
          <div className="relative flex items-center justify-between px-4 py-2.5">
            {/* Left: menu trigger (toggles the panel) */}
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="menu-open-btn"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              className={`flex h-[52px] items-center gap-2.5 transition-opacity hover:opacity-70 ${burgerColor}`}
            >
              <Burger open={open} />
              <span
                className="hidden text-sm font-semibold uppercase tracking-[0.18em] md:inline"
                data-testid="menu-label"
              >
                {ui.menu}
              </span>
            </button>

            {/* Center: logo */}
            <Link
              to="/"
              data-testid="navbar-logo"
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            >
              <img
                src={logoSrc}
                alt="Efi's Apartments"
                className="h-9 w-auto transition-all duration-300 sm:h-11"
              />
            </Link>

            {/* Right: language + book */}
            <div className="flex items-center gap-2 sm:gap-2.5">
              <a
                href={otherLangHref()}
                data-testid="lang-toggle"
                title={LANG === "en" ? "Στα Ελληνικά" : "In English"}
                className={`hidden h-11 min-w-[40px] place-items-center rounded-[10px] px-2 text-[20px] leading-none transition-all hover:-translate-y-0.5 sm:grid sm:h-[52px] sm:min-w-[52px] sm:px-3 sm:text-[28px] ${cardCls}`}
                style={{ fontFamily: '"Instrument Serif", serif', letterSpacing: "0.02em" }}
              >
                {LANG === "en" ? "GR" : "EN"}
              </a>
              <BookButton
                className="!h-11 !rounded-[10px] !text-[18px] !py-0 !px-3 shadow-[0_6px_20px_rgba(251,168,13,0.35)] sm:!h-[52px] sm:!text-[28px] sm:!px-5"
                testid="navbar-book-btn"
              />
            </div>
          </div>
        </motion.div>
      </header>

      {/* Menu panel */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 z-[60] bg-ink/25"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setOpen(false)}
              data-testid="menu-backdrop"
            />
            <motion.aside
              className="fixed left-[10px] top-[10px] z-[61] flex origin-top-left flex-col overflow-hidden rounded-[16px] bg-white px-7 pb-7 shadow-[0_30px_80px_rgba(0,0,0,0.28)]"
              style={{ width: "min(380px, calc(100vw - 20px))", height: "min(840px, calc(100dvh - 20px))" }}
              initial={{ opacity: 0, scale: 0.7, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.7, y: -10 }}
              transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
              data-testid="menu-overlay"
            >
              {/* Spacer so the panel opens naturally around the nav burger */}
              <div className="h-[72px] shrink-0 sm:h-[80px]" aria-hidden="true" />

              <div className="mt-3 flex min-h-0 flex-1 flex-col overflow-y-auto no-scrollbar sm:mt-5">
                <nav className="flex flex-col gap-1">
                  {nav.map((item, i) => {
                    const label = item.to === "/contact" ? ui.contactUs : item.label;
                    const active = pathname === item.to;
                    return (
                      <React.Fragment key={item.to}>
                        <motion.div
                          initial={{ opacity: 0, x: -16 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 + i * 0.05, duration: 0.4 }}
                        >
                          <Link
                            to={item.to}
                            onClick={() => setOpen(false)}
                            data-testid={`menu-link-${item.to === "/" ? "home" : item.to.replace(/^\//, "").replace(/\//g, "-")}`}
                            className={`block nav-link hover:text-golden ${
                              active ? "text-golden" : "text-ink"
                            }`}
                          >
                            {label}
                          </Link>
                        </motion.div>

                        {/* Accommodation — right under Home */}
                        {item.to === "/" && (
                          <motion.div
                            initial={{ opacity: 0, x: -16 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.18, duration: 0.4 }}
                            className="mb-1"
                          >
                            <p className="nav-link text-ink">
                              {ui.accommodation}
                            </p>
                            <div className="mt-1.5 space-y-1 pl-0.5 sm:mt-2 sm:space-y-1.5">
                              {apartments.map((apt) => {
                                const aptActive = pathname === `/apartments/${apt.slug}`;
                                return (
                                  <Link
                                    key={apt.slug}
                                    to={`/apartments/${apt.slug}`}
                                    onClick={() => setOpen(false)}
                                    data-testid={`menu-apt-${apt.slug}`}
                                    aria-label={`${apt.number} ${apt.name}`}
                                    aria-current={aptActive ? "page" : undefined}
                                    className="group flex items-baseline gap-2.5 transition-colors"
                                  >
                                    <span className="font-serif text-[15px] text-golden sm:text-[20px]">{apt.number}</span>
                                    <span className={`text-[16px] leading-snug transition-colors group-hover:text-golden sm:text-[22px] ${aptActive ? "text-golden" : "text-ink/80"}`}>
                                      {apt.name}
                                    </span>
                                  </Link>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </nav>

                <Link
                  to="/booking-policy"
                  onClick={() => setOpen(false)}
                  data-testid="menu-link-booking-policy"
                  className="mt-4 inline-flex w-fit items-center rounded-full bg-azure/25 px-4 py-1.5 font-sans text-sm font-semibold uppercase tracking-[0.14em] text-ink/80 transition-colors hover:bg-azure/40 hover:text-ink sm:mt-6 sm:py-2"
                >
                  {ui.bookingPolicy}
                </Link>

                {/* Book CTA + contact — flows right after the menu so the spacing stays tight */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                  className="mt-5 space-y-3"
                >
                  <div className="flex items-center gap-3 sm:hidden" data-testid="menu-lang">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-ink/45">{ui.language}</span>
                    <div className="flex items-center gap-1.5">
                      <a href={LANG === "en" ? "#" : otherLangHref()} className={`grid h-8 min-w-[44px] place-items-center rounded-full font-serif text-[18px] ${LANG === "en" ? "bg-golden text-ink" : "bg-ink/5 text-ink/50"}`} data-testid="menu-lang-en">EN</a>
                      <a href={LANG === "el" ? "#" : otherLangHref()} className={`grid h-8 min-w-[44px] place-items-center rounded-full font-serif text-[18px] ${LANG === "el" ? "bg-golden text-ink" : "bg-ink/5 text-ink/50"}`} data-testid="menu-lang-gr">GR</a>
                    </div>
                  </div>
                  <div className="rounded-[10px] border border-ink/10 px-5 py-3 sm:py-4">
                    <div className="space-y-1 text-[15px] text-ink/80">
                      <a href={site.phoneHref} className="block hover:text-golden transition-colors">{site.phone}</a>
                      <a href={site.emailHref} className="block hover:text-golden transition-colors">{site.email}</a>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
